import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import AlertModal from "../../Requirement/components/common/AlerModel";
import * as XLSX from "xlsx";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";
import { FormControl, Typography, Select, MenuItem } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

const CustomButton = ({ label, onClick, disabled }) => {
  return (
    <button className="submit-btn-more" onClick={onClick} disabled={disabled}>
      {label}
    </button>
  );
};

// SellerForm Component with Tabbed Interface

const SellerForm = ({
  seller,
  onSave,
  onCancel,
  mode,
  handleSellerResetPassword,
}) => {
  const { setLoading } = useLoading();
  const [countrymaster, setCountryMaster] = useState([]);
  const [formData, setFormData] = useState(
    seller || {
      AMTPLUserID: "",
      EmailID: "",
      GPEmailID: "",
      FirstName: "",
      LastName: "",
      Phone1: "",
      Phone2: "",
      Address1: "",
      Address2: "",
      ZipCode: "",
      Country: "",
      State: "",
      City: "",
      CompanyName: "",
      CompanyZipCode: "",
      CompanyPhone1: "",
      CompanyPhone2: "",
      CompanyPhone3: "",
      CompanyPhone4: "",
      CompanyAddress1: "",
      CompanyAddress2: "",
      CompanyAddress3: "",
      CompanyCity: "",
      CompanyCountry: "",
      CompanyEmail: "",
      CompanyState: "",
      CompanyGSTIN: "",
      Age: "",
      Gender: "",
      CompanyLinkedIn: "",
      CompanyTwitter: "",
      CompanyWebsite: "",
      LinkedIn: "",
      Twitter: "",
      Website: "",
      Status: "ACTIVE",
    }
  );
  const [errors, setErrors] = useState({});

  const [activeTab, setActiveTab] = useState("supplierDetails");

  const requiredFields = [
    "EmailID",
    "FirstName",
    "Phone1",
    "Address1",
    "Country",
  ];

  const validateForm = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].toString().trim() === "") {
        newErrors[field] = `${field} is required`;
      }
    });
    if (formData.EmailID && !/\S+@\S+\.\S+/.test(formData.EmailID)) {
      newErrors.EmailID = "Invalid email format";
    }
    if (formData.Age && !Number.isInteger(Number(formData.Age))) {
      newErrors.Age = "Age must be an integer";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEmail = useCallback(
    async (email) => {
      if (!email) {
        setErrors((prev) => ({ ...prev, EmailID: "Email is required" }));
        return false;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setErrors((prev) => ({
          ...prev,
          EmailID: "Please enter a valid email address",
        }));
        return false;
      }

      if (mode === "add") {
        try {
          setLoading(true);
          const response = await fetchData({
            target: "listAMTPLUsers",
            userOrder: "AMTPLUserID",
            rowStart: 0,
            pageSize: 1,
            filterTerms: { EmailID: email },
          });

          if (response.items && response.items.length > 0) {
            setErrors((prev) => ({
              ...prev,
              EmailID: "This email is already in use",
            }));
            return false;
          }
        } catch (error) {
          console.error(`Error checking email: `, error);
          setErrors((prev) => ({
            ...prev,
            EmailID: "Error checking email availability",
          }));
          return false;
        } finally {
          setLoading(false);
        }
      }

      setErrors((prev) => ({ ...prev, EmailID: "" }));
      return true;
    },
    [mode, setLoading]
  );

  const validatePhone = useCallback((phone) => {
    const phoneRegex = /^\+?[\d\s-]{10,15}$/;
    if (!phone) {
      setErrors((prev) => ({
        ...prev,
        Phone1: "Phone Number is required",
      }));
      return false;
    } else if (!phoneRegex.test(phone)) {
      setErrors((prev) => ({
        ...prev,
        Phone1: "Please enter a valid phone number (10-15 digits)",
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, Phone1: "" }));
    return true;
  }, []);

  const handleChange = (e) => {
    const { id, name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id || name]: value,
    }));
    setErrors((prev) => ({ ...prev, [id || name]: null }));
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // Load countries
  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const handleSubmit = async () => {
    const isFormValid = validateForm();
    const isEmailValid = await validateEmail(formData.EmailID);
    const isPhoneValid = validatePhone(formData.Phone1);

    if (isFormValid && isEmailValid && isPhoneValid) {
      console.log("formData@", formData);
      onSave(formData);
    }
  };

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  return (
    <div className="mt-4">
      <div className="button-group mt-3">
        {mode === "edit" && (
          <CustomButton
            label="Generate & Send Password"
            disabled={!seller}
            onClick={handleSellerResetPassword}
          />
        )}
        <button className="submit-btn-more" onClick={handleSubmit}>
          {mode === "add" ? "Submit" : "Update"}
        </button>
        <button className="submit-btn-more" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <section>
        <div className="tabs">
          <button
            className={activeTab === "supplierDetails" ? "active" : ""}
            onClick={() => handleTabClick("supplierDetails")}
          >
            Partner Details
          </button>
          <button
            className={activeTab === "companyDetails" ? "active" : ""}
            onClick={() => handleTabClick("companyDetails")}
          >
            Company Partner
          </button>
        </div>

        {activeTab === "supplierDetails" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-12 col-sm-12 col-6">
                <div className="row">
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="FirstName">First Name</label>
                    <input
                      id="FirstName"
                      type="text"
                      className="form-control"
                      value={formData.FirstName}
                      onChange={handleChange}
                    />
                    {errors.FirstName && (
                      <Typography variant="caption" color="error">
                        {errors.FirstName}
                      </Typography>
                    )}
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="LastName">Last Name</label>
                    <input
                      id="LastName"
                      type="text"
                      className="form-control"
                      value={formData.LastName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="Age">Age</label>
                    <input
                      id="Age"
                      type="text"
                      className="form-control"
                      value={formData.Age}
                      onChange={handleChange}
                    />
                    {errors.Age && (
                      <Typography variant="caption" color="error">
                        {errors.Age}
                      </Typography>
                    )}
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <FormControl fullWidth>
                      <Typography variant="body2">Gender</Typography>
                      <Select
                        name="Gender"
                        value={formData.Gender || ""}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ backgroundColor: "white" }}
                        inputProps={{ "aria-label": "Select Gender" }}
                      >
                        <MenuItem value="" disabled>
                          Select Gender
                        </MenuItem>
                        <MenuItem value="M">Male</MenuItem>
                        <MenuItem value="F">Female</MenuItem>
                        <MenuItem value="O">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="Phone1">Phone 1</label>
                    <input
                      id="Phone1"
                      type="text"
                      className="form-control"
                      value={formData.Phone1}
                      onChange={handleChange}
                    />
                    {errors.Phone1 && (
                      <Typography variant="caption" color="error">
                        {errors.Phone1}
                      </Typography>
                    )}
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="Phone2">Phone 2</label>
                    <input
                      id="Phone2"
                      type="text"
                      className="form-control"
                      value={formData.Phone2}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="EmailID">Email</label>
                    <input
                      id="EmailID"
                      type="email"
                      className="form-control"
                      value={formData.EmailID}
                      onChange={handleChange}
                    />
                    {errors.EmailID && (
                      <Typography variant="caption" color="error">
                        {errors.EmailID}
                      </Typography>
                    )}
                  </div>

                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="LinkedIn">LinkedIn</label>
                    <input
                      id="LinkedIn"
                      type="text"
                      className="form-control"
                      value={formData.LinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="Twitter">Twitter</label>
                    <input
                      id="Twitter"
                      type="text"
                      className="form-control"
                      value={formData.Twitter}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="Website">Website</label>
                    <input
                      id="Website"
                      type="text"
                      className="form-control"
                      value={formData.Website}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    {/* <label htmlFor="Country">Country</label>
                    <input
                      id="Country"
                      type="text"
                      className="form-control"
                      value={formData.Country || "India"}
                      onChange={handleChange}
                    /> */}
                    <FormControl
                      sx={{ minWidth: "100%" }}
                      error={!!errors.Country}
                    >
                      <Typography variant="body2">Country</Typography>
                      <Select
                        name="Country"
                        value={formData.Country || ""}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ backgroundColor: "white" }}
                        inputProps={{ "aria-label": "Select Country" }}
                        error={!!errors.Country}
                      >
                        <MenuItem value="" disabled>
                          Select country
                        </MenuItem>
                        {countrymaster.map((p) => (
                          <MenuItem key={p.ID} value={p.Value}>
                            {p.Value}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Country && (
                        <Typography variant="caption" color="error">
                          {errors.Country}
                        </Typography>
                      )}
                    </FormControl>
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="State">State</label>
                    <input
                      id="State"
                      type="text"
                      className="form-control"
                      value={formData.State}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="Address1">Address</label>
                    <input
                      id="Address1"
                      type="text"
                      className="form-control"
                      value={formData.Address1}
                      onChange={handleChange}
                    />
                    {errors.Address1 && (
                      <Typography variant="caption" color="error">
                        {errors.Address1}
                      </Typography>
                    )}
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="ZipCode">PIN Code</label>
                    <input
                      id="ZipCode"
                      type="text"
                      className="form-control"
                      value={formData.ZipCode}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <FormControl
                      sx={{ minWidth: "100%" }}
                      error={!!errors.Status}
                    >
                      <Typography variant="body2">Status</Typography>
                      <Select
                        name="Status"
                        value={formData.Status}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ backgroundColor: "white" }}
                        inputProps={{ "aria-label": "Select Status" }}
                      >
                        <MenuItem value="" disabled>
                          Select Status
                        </MenuItem>
                        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                        <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                      </Select>
                      {errors.Status && (
                        <Typography variant="caption" color="error">
                          {errors.Status}
                        </Typography>
                      )}
                    </FormControl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "companyDetails" && (
          <div className="tab-content active">
            <div className="row">
              <div className="col-lg-12 col-sm-12 col-6">
                <div className="row">
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyName">Co Name</label>
                    <input
                      id="CompanyName"
                      type="text"
                      className="form-control"
                      value={formData.CompanyName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyWebsite">Website</label>
                    <input
                      id="CompanyWebsite"
                      type="text"
                      className="form-control"
                      value={formData.CompanyWebsite}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyEmail">Email</label>
                    <input
                      id="CompanyEmail"
                      type="text"
                      className="form-control"
                      value={formData.CompanyEmail}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyCountry">Country</label>
                    <input
                      id="CompanyCountry"
                      type="text"
                      className="form-control"
                      value={formData.CompanyCountry}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyState">State</label>
                    <input
                      id="CompanyState"
                      type="text"
                      className="form-control"
                      value={formData.CompanyState}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyAddress1">Address</label>
                    <input
                      id="CompanyAddress1"
                      type="text"
                      className="form-control"
                      value={formData.CompanyAddress1}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyZipCode">PIN Code</label>
                    <input
                      id="CompanyZipCode"
                      type="text"
                      className="form-control"
                      value={formData.CompanyZipCode}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyLinkedIn">LinkedIn</label>
                    <input
                      id="CompanyLinkedIn"
                      type="text"
                      className="form-control"
                      value={formData.CompanyLinkedIn}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyTwitter">Twitter</label>
                    <input
                      id="CompanyTwitter"
                      type="text"
                      className="form-control"
                      value={formData.CompanyTwitter}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyWebsite2">Website</label>
                    <input
                      id="CompanyWebsite"
                      type="text"
                      className="form-control"
                      value={formData.CompanyWebsite}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyPhone1">Phone 1</label>
                    <input
                      id="CompanyPhone1"
                      type="text"
                      className="form-control"
                      value={formData.CompanyPhone1}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyPhone2">Phone 2</label>
                    <input
                      id="CompanyPhone2"
                      type="text"
                      className="form-control"
                      value={formData.CompanyPhone2}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyPhone3">Phone 3</label>
                    <input
                      id="CompanyPhone3"
                      type="text"
                      className="form-control"
                      value={formData.CompanyPhone3}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-lg-3 col-sm-4 col-6">
                    <label htmlFor="CompanyPhone4">Phone 4</label>
                    <input
                      id="CompanyPhone4"
                      type="text"
                      className="form-control"
                      value={formData.CompanyPhone4}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};
export default SellerForm;